/**
 * Générateur — Enchaîner additions et soustractions de décimaux relatifs.
 * Attendu ID : 713 | Niveau : 5ème | Domaine : Nombres relatifs (cluster 5)
 *
 * Plan de cartes :
 *   « gauche » (Ex.5) : transformer puis calculer DE GAUCHE À DROITE, avec
 *     les résultats intermédiaires en trous (saisie).
 *   « etapes » (Ex.7bis) : enchaînements en écriture simplifiée, 3 termes,
 *     étape intermédiaire + résultat (saisie).
 *   « astuce » (Ex.8bis) : regrouper astucieusement — qcm_unique (la paire
 *     qui s'annule, UNIQUE par construction) + texte_trou_saisie (résultat,
 *     compléments à deux décimales type 13,36 − 3,36 : automatisme B1).
 *   « mixte » (Ex.4bis) : les deux écritures mélangées dans une même
 *     expression (niveau de synthèse) — résultat en saisie.
 *
 * Arithmétique en CENTIÈMES entiers (11,95 + 0,05…). Signes imposés.
 */

import {
    randomChoice, randInt, shuffle,
    makeQcm, makeTexteTrou, filtrerDistracteurs,
} from '../../../utils';

const ATTENDU_ID = 713;
const L_GAUCHE = 'Calculer de gauche à droite';
const L_ETAPES = 'Enchaînements en écriture simplifiée';
const L_ASTUCE = 'Regrouper astucieusement';
const L_MIXTE = 'Enchaînements mixtes';

const opts = (label) => ({ niveau: '5eme', matiere: 'maths', attendu: ATTENDU_ID, label });

// centièmes → écriture française ("13,36", "-0,6", "108")
const frC = (c) => {
    const s = c < 0 ? '-' : '';
    const a = Math.abs(c);
    const ent = Math.floor(a / 100);
    const frac = String(a % 100).padStart(2, '0').replace(/0+$/, '');
    return s + (frac ? `${ent},${frac}` : String(ent));
};
const signeC = (c) => (c === 0 ? '0' : c < 0 ? frC(c) : `+${frC(c)}`);
const parenC = (c) => `(${signeC(c)})`;

const CONSIGNE_RES = 'Écris chaque réponse avec son signe (ex. : -5 ou +8), ou 0 si elle est nulle.';

// ═════════ Carte 1 — de gauche à droite (Ex.5) ═════════

const genGaucheSaisie = () => {
    const contribs = [];
    const absolues = new Set();
    for (let essai = 0; essai < 60 && contribs.length < 4; essai++) {
        const v = randInt(2, 35) * 100;
        if (!absolues.has(v)) { absolues.add(v); contribs.push((randomChoice([true, false]) ? 1 : -1) * v); }
    }
    [400, -800, 2000, -2700].forEach(v => {
        if (contribs.length < 4 && !absolues.has(Math.abs(v))) { absolues.add(Math.abs(v)); contribs.push(v); }
    });
    const ops = [null, ...[1, 2, 3].map(() => randomChoice(['+', '-']))];
    const termes = contribs.map((c, i) => (i === 0 ? c : (ops[i] === '+' ? c : -c)));
    const morceau = (i) => `${ops[i]} ${parenC(termes[i])}`;
    const expr = `${parenC(termes[0])} ${morceau(1)} ${morceau(2)} ${morceau(3)}`;
    const p1 = contribs[0] + contribs[1];
    const p2 = p1 + contribs[2];
    const p3 = p2 + contribs[3];
    const phrase = `A = ${expr} — de gauche à droite : A = {{${signeC(p1)}}} ${morceau(2)} ${morceau(3)} ; `
        + `A = {{${signeC(p2)}}} ${morceau(3)} ; A = {{${signeC(p3)}}}`;
    const payload = makeTexteTrou(phrase,
        `<p><b>Transforme les soustractions en additions de l'opposé, puis calcule de gauche à droite.</b> ${CONSIGNE_RES}</p>`,
        { ...opts(L_GAUCHE), mode: 'saisie' });
    payload._meta.params = { termes, ops };
    return payload;
};

// ═════════ Carte 2 — écriture simplifiée avec étapes (Ex.7bis) ═════════

const genEtapesSaisie = () => {
    const decimal = randomChoice([true, false]);
    const v = () => (decimal ? randInt(11, 99) * 10 + randomChoice([0, randInt(1, 9) * 10]) : randInt(2, 27) * 100);
    const c1 = (randomChoice([true, false]) ? 1 : -1) * v();
    let c2 = (randomChoice([true, false]) ? 1 : -1) * v();
    if (Math.abs(c2) === Math.abs(c1)) c2 += 100;
    const c3 = (randomChoice([true, false]) ? 1 : -1) * v();
    const aff = (c, i) => (i === 0 ? frC(c) : `${c > 0 ? '+' : '-'} ${frC(Math.abs(c))}`);
    const expr = `${aff(c1, 0)} ${aff(c2, 1)} ${aff(c3, 2)}`;
    const phrase = `E = ${expr} ; E = {{${signeC(c1 + c2)}}} ${aff(c3, 2)} ; E = {{${signeC(c1 + c2 + c3)}}}`;
    const payload = makeTexteTrou(phrase,
        `<p><b>Effectue le calcul en deux étapes, de gauche à droite.</b> ${CONSIGNE_RES}</p>`,
        { ...opts(L_ETAPES), mode: 'saisie' });
    payload._meta.params = { c1, c2, c3 };
    return payload;
};

// ═════════ Carte 3 — regrouper astucieusement (Ex.8bis) ═════════

/** 5 contributions dont UNE paire opposée (parfois à deux décimales, B1). */
const tirerAstuce = () => {
    const deuxDec = randomChoice([true, false]);
    const x = deuxDec ? randInt(3, 19) * 100 + randInt(1, 99) : randInt(3, 89) * 10;
    const absolues = new Set([Math.abs(x)]);
    const autres = [];
    for (let essai = 0; essai < 60 && autres.length < 3; essai++) {
        const v = (randomChoice([true, false]) ? 1 : -1) * randInt(2, 45) * 100;
        if (!absolues.has(Math.abs(v))) { absolues.add(Math.abs(v)); autres.push(v); }
    }
    [400, -900, 1300].forEach(v => {
        if (autres.length < 3 && !absolues.has(Math.abs(v))) { absolues.add(Math.abs(v)); autres.push(v); }
    });
    const contribs = shuffle([x, -x, ...autres]);
    const expr = contribs.map((c, i) => (i === 0 ? frC(c) : `${c > 0 ? '+' : '-'} ${frC(Math.abs(c))}`)).join(' ');
    return { x, contribs, expr, reste: autres.reduce((s, v) => s + v, 0) };
};

const genAstuceQcm = () => {
    const { x, contribs, expr } = tirerAstuce();
    const [p1, p2] = contribs.filter(c => Math.abs(c) === Math.abs(x));
    const correct = `${frC(p1)} et ${frC(p2)}`;
    const faux = [];
    const indices = shuffle(contribs.flatMap((_, i) => contribs.map((__, j) => [i, j]).filter(([u, w]) => u < w)));
    for (const [i, j] of indices) {
        if (faux.length >= 3) break;
        if (contribs[i] + contribs[j] !== 0) {
            const t = `${frC(contribs[i])} et ${frC(contribs[j])}`;
            if (!faux.includes(t)) faux.push(t);
        }
    }
    const payload = makeQcm([correct, ...faux], correct,
        `<p>On veut calculer <strong>${expr}</strong> astucieusement. `
        + '<b>Quels sont les deux termes opposés (somme nulle) à regrouper d\'abord ?</b></p>',
        opts(L_ASTUCE));
    payload._meta.params = { x, contribs };
    return payload;
};

const genAstuceSaisie = () => {
    const { contribs, expr, reste } = tirerAstuce();
    const phrase = `P = ${expr} ; en regroupant astucieusement, P = {{${signeC(reste)}}}`;
    const payload = makeTexteTrou(phrase,
        `<p><b>Regroupe astucieusement (repère les opposés), puis calcule.</b> ${CONSIGNE_RES}</p>`,
        { ...opts(L_ASTUCE), mode: 'saisie' });
    payload._meta.params = { contribs, reste };
    return payload;
};

// ═════════ Carte 4 — enchaînements mixtes (Ex.4bis) ═════════

const genMixteSaisie = () => {
    const n = randomChoice([4, 5]);
    const contribs = [];
    const absolues = new Set();
    for (let essai = 0; essai < 80 && contribs.length < n; essai++) {
        const v = randomChoice([true, false]) ? randInt(2, 35) * 100 : randInt(11, 99) * 10;
        if (!absolues.has(v)) { absolues.add(v); contribs.push((randomChoice([true, false]) ? 1 : -1) * v); }
    }
    [-310, 900, -840, -730, 1200].forEach(v => {
        if (contribs.length < n && !absolues.has(Math.abs(v))) { absolues.add(Math.abs(v)); contribs.push(v); }
    });
    // mélange des deux écritures : nu simplifié OU « op (relatif) »
    const morceaux = contribs.map((c, i) => {
        if (i === 0) return frC(c);
        if (randomChoice([true, false])) return `${c > 0 ? '+' : '-'} ${frC(Math.abs(c))}`;
        const op = randomChoice(['+', '-']);
        return `${op} ${parenC(op === '+' ? c : -c)}`;
    });
    const total = contribs.reduce((s, v) => s + v, 0);
    const phrase = `B = ${morceaux.join(' ')} ; B = {{${signeC(total)}}}`;
    const payload = makeTexteTrou(phrase,
        `<p><b>Effectue le calcul.</b> ${CONSIGNE_RES}</p>`,
        { ...opts(L_MIXTE), mode: 'saisie' });
    payload._meta.params = { contribs };
    return payload;
};

// ─── Registre ────────────────────────────────────────────────
const GENERATORS = {
    gauche_texte_trou_saisie: genGaucheSaisie,
    etapes_texte_trou_saisie: genEtapesSaisie,
    astuce_opposes_qcm_unique: genAstuceQcm,
    astuce_texte_trou_saisie: genAstuceSaisie,
    mixte_texte_trou_saisie: genMixteSaisie,
};

const FORMAT_LABELS = {
    texte_trou_saisie: 'Saisie trous', opposes_qcm_unique: 'QCM Opposés',
};
const buildFormats = (prefix) =>
    Object.keys(GENERATORS).filter(k => k.startsWith(`${prefix}_`))
        .map(k => ({ key: k, label: FORMAT_LABELS[k.slice(prefix.length + 1)] || k }));

export const CATEGORIES = [
    { id: 'gauche', label: L_GAUCHE, formatsList: buildFormats('gauche') },
    { id: 'etapes', label: L_ETAPES, formatsList: buildFormats('etapes') },
    { id: 'astuce', label: L_ASTUCE, formatsList: buildFormats('astuce') },
    { id: 'mixte', label: L_MIXTE, formatsList: buildFormats('mixte') },
];

export const generate = (typeExercice) => {
    let key;
    if (typeExercice && GENERATORS[typeExercice]) key = typeExercice;
    else if (typeExercice) {
        const matching = Object.keys(GENERATORS).filter(k => k.startsWith(typeExercice) || k.includes(typeExercice));
        key = matching.length ? randomChoice(matching) : randomChoice(Object.keys(GENERATORS));
    } else key = randomChoice(Object.keys(GENERATORS));
    const payload = GENERATORS[key]();
    payload._meta.generatorKey = key;
    return payload;
};
